import { useState } from 'react';

// Default allowlist for Mercury document URLs.
export const DEFAULT_ALLOWED_DOMAINS = ['microsoft.sharepoint.com'];

// Types for the new hook API
export interface UseUrlValidatorOptions {
  allowedDomains?: string[];
}

export interface UseUrlValidatorResult {
  /**
   * Validate and sanitize the provided input. Returns the canonical URL string when valid, otherwise null.
   */
  validateAndSanitize: (input: string) => string | null;
  /** Latest validation error (null if last validation succeeded). */
  error: string | null;
  /** Last successfully sanitized (canonical) URL string. */
  sanitizedUrl: string | null;
}

// Helper: normalize a domain (trim, lowercase, strip single trailing dot)
export function normalizeDomain(d: string): string {
  return d.trim().toLowerCase().replace(/\.$/, '');
}

function hasSafeHttpSyntax(value: string): boolean {
  return /^https?:\/\//i.test(value) && !value.includes('\\') && !/[\t\r\n]/.test(value);
}

/**
 * Hook: useUrlValidator
 * Requirements implemented:
 *  - trims input
 *  - validates absolute HTTP/HTTPS URLs via the URL constructor
 *  - optional allowlist (domain or any of its subdomains)
 *  - returns canonicalized url.toString() (keeps query + fragment intact)
 *  - does NOT perform SSRF / IP blocking logic (explicitly excluded)
 */
export function useUrlValidator({
  allowedDomains = DEFAULT_ALLOWED_DOMAINS,
}: UseUrlValidatorOptions = {}): UseUrlValidatorResult {
  const [error, setError] = useState<string | null>(null);
  const [sanitizedUrl, setSanitizedUrl] = useState<string | null>(null);

  const validateAndSanitize = (input: string): string | null => {
    const raw = input ?? '';
    const trimmed = raw.trim();

    // Allow empty input to be considered "no value" without error (required logic handled at form level)
    if (!trimmed) {
      setError(null);
      setSanitizedUrl(null);
      return null;
    }

    const result = sanitizeUrl(trimmed, allowedDomains);
    if (!result.isValid) {
      setError(result.message || 'Invalid URL format');
      setSanitizedUrl(null);
      return null;
    }

    setError(null);
    setSanitizedUrl(result.sanitizedUrl);
    return result.sanitizedUrl;
  };

  return { validateAndSanitize, error, sanitizedUrl };
}

/**
 * Sanitizes and validates an HTTP(S) URL.
 * Client-side validation for UX - server-side validation is mandatory for security
 */
export interface SanitizationResult {
  sanitizedUrl: string;
  isValid: boolean;
  message?: string;
}

/**
 * Stateless utility retained for existing external uses (if any).
 * Mirrors the new hook semantics (does NOT auto-prepend protocol).
 */
export function sanitizeUrl(url: string, allowedDomains: string[] = []): SanitizationResult {
  const trimmed = (url || '').trim();
  if (!trimmed) {
    return { sanitizedUrl: '', isValid: false, message: 'URL is required' };
  }
  try {
    const u = new URL(trimmed);
    if (!['http:', 'https:'].includes(u.protocol)) {
      return {
        sanitizedUrl: trimmed,
        isValid: false,
        message: 'Only http and https URLs are allowed.',
      };
    }
    if (!hasSafeHttpSyntax(trimmed)) {
      return {
        sanitizedUrl: trimmed,
        isValid: false,
        message: 'Invalid URL. Please provide an absolute http(s) URL.',
      };
    }
    if (allowedDomains.length) {
      const normalizedAllowedDomains = allowedDomains.map(normalizeDomain);
      const host = u.hostname.toLowerCase().replace(/\.$/, '');
      const isAllowed = normalizedAllowedDomains.some(
        (domain) => host === domain || host.endsWith(`.${domain}`),
      );
      if (!isAllowed) {
        return {
          sanitizedUrl: trimmed,
          isValid: false,
          message: `Domain not allowed. Allowed domains: ${normalizedAllowedDomains.join(', ')}`,
        };
      }
    }
    return { sanitizedUrl: u.toString(), isValid: true };
  } catch {
    return {
      sanitizedUrl: trimmed,
      isValid: false,
      message: 'Invalid URL. Please provide an absolute http(s) URL.',
    };
  }
}

/**
 * Creates safe props for anchor elements in React
 * Always use this when rendering user-provided URLs
 */
export function createSafeUrlProps(url: string): {
  href?: string;
  rel: string;
  target: string;
  isValid: boolean;
} {
  const validation = sanitizeUrl(url);
  return {
    href: validation.isValid ? validation.sanitizedUrl : undefined,
    rel: 'noopener noreferrer',
    target: '_blank',
    isValid: validation.isValid,
  };
}
